import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Plus,
  BarChart2,
  Eye,
  Video,
  ChevronRight,
  Film,
  Grid,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SideBar from '../../components/SideBar';
import authApi from '../../auth.api';
import {
  ACTION_SERIES,
  CREATE_SERIES,
  DELETE_VIDEO,
  GET_ACTIVE_SERIES,
  GET_SERIES,
  GET_SHORTS_GENERATED,
  GET_USER,
  GET_VIDEOS,
  UPGRADE_PLAN,
  UPLOAD_TO_INSTAGRAM,
  UPLOAD_TO_TIKTOK,
  UPLOAD_TO_YOUTUBE,
} from '../../route';
import MySeriesTableCard from '../../components/series/my-series/table/MySeriesTableCard';
import VideoShowcase from '../../components/series/my-series/video-showcase/VideoShowcase';

const MySeriesCard = ({ children, className }) => (
  <div className={`bg-white rounded-lg shadow-sm p-6 ${className}`}>
    {children}
  </div>
);

const MySeriesButton = ({
  children,
  variant = 'default',
  onClick,
  className,
}) => {
  const baseClasses =
    'px-4 py-2 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200';
  const variantClasses = {
    default:
      'bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500',
    ghost: 'text-gray-600 hover:bg-gray-100 focus:ring-gray-500',
    outline:
      'border border-gray-300 text-gray-700 hover:bg-gray-50 focus:ring-gray-500',
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${className}`}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const StatCard = ({ title, value, icon: Icon, trend }) => (
  <MySeriesCard className='flex items-center justify-between'>
    <div className='flex items-center'>
      <div className='p-3 bg-indigo-100 rounded-full mr-4'>
        <Icon className='h-6 w-6 text-indigo-600' />
      </div>
      <div>
        <h4 className='text-sm font-medium text-gray-500'>{title}</h4>
        <p className='text-2xl font-bold text-gray-800'>{value}</p>
      </div>
    </div>
    {trend && (
      <div
        className={`flex items-center ${
          trend > 0 ? 'text-green-500' : 'text-red-500'
        }`}
      >
        <BarChart2
          className={`h-4 w-4 mr-1 ${trend < 0 ? 'transform rotate-180' : ''}`}
        />
        <span className='text-sm font-medium'>{Math.abs(trend)}%</span>
      </div>
    )}
  </MySeriesCard>
);

const QuickActionCard = ({ icon: Icon, title, description, onClick }) => (
  <motion.div
    whileHover={{ y: -5, boxShadow: '0 10px 30px -15px rgba(0, 0, 0, 0.1)' }}
    transition={{ duration: 0.2 }}
    className='bg-white rounded-lg shadow-sm p-6 group cursor-pointer'
    onClick={onClick}
  >
    <div className='flex items-center mb-4'>
      <div className='p-3 bg-indigo-100 rounded-full mr-4 group-hover:bg-indigo-200 transition-colors duration-300'>
        <Icon className='h-6 w-6 text-indigo-600' />
      </div>
      <h4 className='text-lg font-semibold text-gray-800 group-hover:text-indigo-600 transition-colors duration-300'>
        {title}
      </h4>
    </div>
    <p className='text-gray-600'>{description}</p>
    <motion.div
      className='mt-4 flex items-center text-indigo-600 font-medium'
      initial={{ x: 0 }}
      whileHover={{ x: 5 }}
    >
      Get Started <ChevronRight className='h-4 w-4 ml-1' />
    </motion.div>
  </motion.div>
);

const UpgradeModal = ({ isOpen, onClose, onUpgrade }) => {
  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
      <div className='bg-white p-6 rounded-lg shadow-lg max-w-md w-full'>
        <h3 className='text-xl font-semibold text-gray-900 mb-4'>
          Upgrade Your Plan
        </h3>
        <p className='mb-4 text-sm text-gray-500'>
          Free plan is limited to 1 active series. Upgrade to create more!
        </p>
        <div className='space-y-4 mb-6'>
          <MySeriesButton onClick={onUpgrade} className='w-full'>
            Upgrade Now
          </MySeriesButton>
        </div>
        <div className='flex justify-end'>
          <MySeriesButton variant='ghost' onClick={onClose}>
            Maybe Later
          </MySeriesButton>
        </div>
      </div>
    </div>
  );
};

const MySeries = () => {
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [videos, setVideos] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [isFreeTier, setIsFreeTier] = useState(true);
  const [currentPlanName, setCurrentPlNAname] = useState('Free Plan');
  const [shortsGenerated, setShortsGenerated] = useState(0);
  const [viewMode, setViewMode] = useState('series');
  const toggleViewMode = () => {
    setViewMode(viewMode === 'series' ? 'showcase' : 'series');
  };
  const [activeSeriesCount, setActiveSeriesCount] = useState(0);
  useEffect(() => {
    fetchSeries();
    fetchVideos();
    fetchShorts();
    fetchActiveSeries();
    fetchUser();
  }, []);
  const fetchShorts = async () => {
    try {
      const { data } = await authApi.get(GET_SHORTS_GENERATED);
      setShortsGenerated(data.count);
    } catch (error) {
      toast.error('Failed to load series. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const fetchActiveSeries = async () => {
    setIsLoading(true);
    try {
      const { data } = await authApi.get(GET_ACTIVE_SERIES);
      setActiveSeriesCount(data.series.length);
    } catch (error) {
      toast.error('Failed to load series. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const fetchUser = async () => {
    setIsLoading(true);
    try {
      const { data } = await authApi.get(GET_USER);
      setCurrentPlNAname(
        data.user.plan.startsWith('Free')
          ? data.user.plan
          : `${data.user.plan} Plan`
      );
      setIsFreeTier(data.user.plan.startsWith('Free'));
    } catch (error) {
      toast.error('Failed to load series. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const stats = [
    { title: 'Active Series', value: activeSeriesCount, icon: Eye },
    { title: 'Total Shorts', value: shortsGenerated, icon: Video },
  ];
  const handleCreateSeries = () => {
    setIsUpgradeModalOpen(true);
    const hasmULTIPLES = series.filter((o) => (o.status = 'Active'));
    if (hasmULTIPLES.length >= 1) {
      setIsUpgradeModalOpen(true);
    } else {
      navigate(CREATE_SERIES); // Navigate to the upgrade page
    }
  };
  const handleUpgradeClick = () => {
    navigate(UPGRADE_PLAN); // Navigate to the upgrade page
  };
  const [seriesIdToDelete, setSeriesIdToDelete] = useState(null);
  const [videoIdToDelete, setVideoIdToDelete] = useState(null);
  const handleDeleteConfirm = async () => {
    try {
      await authApi.delete(`${ACTION_SERIES}${seriesIdToDelete}`);
      await fetchSeries();
      await fetchVideos();
      setIsDeleteModalOpen(false);
      toast.success('Series deleted successfully');
    } catch (error) {
      toast.error('Failed to delete series. Please try again.');
    } finally {
      setIsDeleteModalOpen(false);
      setSeriesIdToDelete(null);
      setSeriesNameToDelete('');
    }
  };
  const handleDeleteVideoConfirm = async () => {
    const result = await authApi.delete(`${DELETE_VIDEO}${videoIdToDelete}`);
    if (result.status) {
      await fetchSeries();
      await fetchVideos();
      toast.success('Series deleted successfully');
      setIsDeleteVideoModalOpen(false);
      setVideoIdToDelete(null);
      setVideoNameToDelete('');
    }
  };
  const handleSeriesAction = async (id, action, seriesName) => {
    //fetch videos again after deleting series
    if (action === 'pause') {
      await authApi.put(`${ACTION_SERIES}pause/${id}`);
      await fetchActiveSeries();
      return fetchSeries();
    }
    if (action === 'resume') {
      await authApi.put(`${ACTION_SERIES}resume/${id}`);
      await fetchActiveSeries();
      return fetchSeries();
    }
    if (action === 'delete') {
      setSeriesIdToDelete(id);
      setSeriesNameToDelete(seriesName);
      setIsDeleteModalOpen(true);
      return; // Don't proceed with deletion yet
    }
    return fetchSeries();
  };
  const [uploadingToYoutube, setUploadingToYoutube] = useState(false);
  const [uploadingToInstagram, setUploadingToInstagram] = useState(false);
  const [uploadingToTikTok, setUploadingToTiktok] = useState(false);
  const handleVideoAction = async (id, action, videoName) => {
    if (action === 'delete') {
      setVideoIdToDelete(id);
      setIsDeleteVideoModalOpen(true);
      setVideoNameToDelete(videoName);
      return; // Don't proceed with deletion yet
    }
    if (action === 'upload to TikTok') {
      setUploadingToTiktok(true);
      toast.success('Queued for upload');
      const result = await authApi.put(`${UPLOAD_TO_TIKTOK}/${id}`);
      if (result.status) {
        setUploadingToTiktok(false);
        toast.success('Tiktok upload successful!');
        return fetchVideos();
      }
    }
    if (action === 'upload to YouTube') {
      setUploadingToYoutube(true);
      toast.success('Queued for upload');
      const result = await authApi.put(`${UPLOAD_TO_YOUTUBE}/${id}`);
      if (result.status) {
        setUploadingToYoutube(false);
        toast.success('Youtube upload successful!');
        return fetchVideos();
      }
    }
    if (action === 'upload to Instagram') {
      setUploadingToInstagram(true);
      toast.success('Queued for upload');
      const result = await authApi.post(`${UPLOAD_TO_INSTAGRAM}/${id}`);
      if (result.status) {
        setUploadingToInstagram(false);
        toast.success('Instagram upload successful!');
        return fetchVideos();
      }
    }
    return fetchVideos();
  };
  const fetchSeries = async () => {
    setIsLoading(true);
    try {
      const { data } = await authApi.get(GET_SERIES);
      setSeries(data.series);
    } catch (error) {
      toast.error('Failed to load series. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const fetchVideos = async () => {
    setIsLoading(true);
    try {
      const { data } = await authApi.get(GET_VIDEOS);
      setVideos(data.videos);
    } catch (error) {
      setIsLoading(false);
      toast.error('Failed to load series. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteVideoModalOpen, setIsDeleteVideoModalOpen] = useState(false);
  const [setSelectedSeries] = useState(null);
  const [seriesNameToDelete, setSeriesNameToDelete] = useState('');
  const [videoNameToDelete, setVideoNameToDelete] = useState('');
  const [editingSeries, setEditingSeries] = useState(null);
  const [publishTime, setPublishTime] = useState({ hour: 16, minute: 0 });
  const handleSave = async (updatedSeries) => {
    console.log(publishTime, 'thepublishTime')
    const seriesRequestData = {
      theme: updatedSeries.contentType,
      platforms: updatedSeries.platforms.map((one) => one),
      voice: updatedSeries.voice.id,
      language: updatedSeries.language,
      publishTime: publishTime.hour || updatedSeries.publishTime,
      artStyle: updatedSeries.artStyle
    };
    if (updatedSeries.customTopic && updatedSeries.customTopic.length > 0) {
      seriesRequestData.customTopic = updatedSeries.customTopic;
    }
    console.log(seriesRequestData, 'seriesRequestData')
    // const response = await authApi.put(`${EDIT_SERIES}/${updatedSeries._id}`, {
    //   series: seriesRequestData
    // });
    // if (response.status) {
    //   toast.success('series edited successfully')
    //   setEditingSeries(null);
    // }
    // toast.error('Unable to edit series.');
  };
  const handlePublishTimeChange = (e) => {
    const [hour, minute] = e.target.value.split(':');
    setPublishTime({ hour: parseInt(hour), minute: parseInt(minute) });
  };

  return <div className='flex h-screen bg-gray-50'>
      <SideBar />
      <div className='flex-1 overflow-auto px-6 py-8 md:p-8'>
        <div className='max-w-7xl mx-auto space-y-8'>
          {/* Header */}
          <div className='flex items-center justify-between'>
            <h2 className='text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 tracking-tight'>
              {viewMode === 'series' ? 'My Series' : 'My Videos'}
            </h2>
            {videos.length > 0 && (
              <MySeriesButton
                onClick={toggleViewMode}
                className='inline-flex items-center px-4 py-2 text-sm'
              >
                {viewMode === 'series' && series.length > 0 ? (
                  <>
                    <Film className='mr-2' size={20} />
                    View Videos
                  </>
                ) : (
                  <>
                    <Grid className='mr-2' size={20} />
                    View Series
                  </>
                )}
              </MySeriesButton>
            )}
          </div>

          {/* Main Content */}
          {viewMode === 'series' ? (
            <div className='space-y-8'>
              {/* Stats Grid */}
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6'>
                {stats.map((stat, index) => (
                  <StatCard key={index} {...stat} />
                ))}
              </div>

              {/* Quick Action Section */}
              <MySeriesCard>
                <h3 className='text-xl md:text-2xl font-semibold mb-4 md:mb-6 text-gray-800'>
                  Quick Action
                </h3>
                <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6'>
                  <QuickActionCard
                    icon={Plus}
                    title='Create New Series'
                    description='Start a new AI-generated series'
                    onClick={handleCreateSeries}
                  />
                </div>
              </MySeriesCard>

              {/* Series Table */}
              <MySeriesTableCard
                  setEditingSeries={setEditingSeries}
                  editingSeries={editingSeries}
                  handleSave={handleSave}
                series={series}
                setSelectedSeries={setSelectedSeries}
                isDeleteModalOpen={isDeleteModalOpen}
                handleDeleteConfirm={handleDeleteConfirm}
                handleSeriesAction={handleSeriesAction}
                seriesNameToDelete={seriesNameToDelete}
                setSeriesNameToDelete={setSeriesNameToDelete}
                setIsDeleteModalOpen={setIsDeleteModalOpen}
                  handlePublishTimeChange={handlePublishTimeChange}
                  publishTime={publishTime}
              />
            </div>
          ) : (
            <VideoShowcase
              videoNameToDelete={videoNameToDelete}
              setVideoNameToDelete={setVideoNameToDelete}
              handleVideoAction={handleVideoAction}
              videos={videos}
              setIsDeleteVideoModalOpen={setIsDeleteVideoModalOpen}
              isDeleteVideoModalOpen={isDeleteVideoModalOpen}
              handleDeleteVideoConfirm={handleDeleteVideoConfirm}
            />
          )}

          {/* Plan Info Card */}
          <motion.div
            className='p-4 md:p-6 bg-white rounded-lg shadow-sm'
            whileHover={{ scale: 1.01 }}
            transition={{ type: 'spring', stiffness: 300 }}
          >
            <div className='flex items-center justify-between'>
              <div>
                <h3 className='text-lg font-semibold text-gray-800 mb-2'>
                  Your Current Plan: {currentPlanName}
                </h3>
              </div>
              {isFreeTier && (
                <MySeriesButton
                  variant='default'
                  onClick={handleUpgradeClick}
                  className='whitespace-nowrap'
                >
                  Upgrade Now
                </MySeriesButton>
              )}
            </div>
          </motion.div>
        </div>
        <UpgradeModal
          isOpen={isUpgradeModalOpen}
          onClose={() => setIsUpgradeModalOpen(false)}
          onUpgrade={handleUpgradeClick}
        />
      </div>{' '}
    </div>

};

export default MySeries;
